import { noop } from 'lodash';
import useFilters from './useFilters';
import DataTableWrapper from './DataTableWrapper';
import {
    buildColumnByDataType,
    convertBooleansToStringMultiselect,
    convertStringDateToJsDates,
    decorateMultiselectColumnsWithOptions,
} from './primeUtils';
import { formatDate, formatDateTime } from './columnBuilder';
import { Button } from '@mui/material';
import { useContext, useEffect, useRef } from 'react';
import { ReportContext } from '../../context/ReportContext';
import { MenuContext } from '../../context/MenuContext';

const StandardizedDataTable = ({
    data,
    columns,
    onValueChange = noop,
    rootClassName = '',
}) => {
    const { setResetFiltersFunc, showHiddenColumns, toggleHiddenColumns, appliedTimeZone } =
        useContext(ReportContext);
    const { isMenuOpen } = useContext(MenuContext);

    // We're going to be doing some in-place manipulation of data to make things
    // easy so to keep that kosher we only operate over a duplicated set of the
    // passed in data
    const dataCopy = data.map((x) => ({ ...x }));
    const columnsCopy = columns.map((x) => ({
        ...x,
        name: x.name?.replace('{tz}', appliedTimeZone),
    }));

    const [filters, setFilters, resetFilters] = useFilters(columnsCopy);
    useEffect(() => {
        setResetFiltersFunc(resetFilters);
    }, []);

    const onFilter = (e) => setFilters(e.filters);

    // Intercept any boolean types and convert the booleans into
    // strings for 'Yes' and 'No', then set the data type to multiselect
    convertBooleansToStringMultiselect(dataCopy, columnsCopy);

    // Decorate the multi-selectors with an array of all the unique options
    // from the data to serve as options in the filter
    decorateMultiselectColumnsWithOptions(dataCopy, columnsCopy);

    // Convert the date's in the data to JS date objects
    // This uses the default parser so must ensure all dates are in
    // a standard ISO shape
    convertStringDateToJsDates(dataCopy, columnsCopy, appliedTimeZone);

    // Assemble the DataTable friendly <Column>'s
    const childrenColumns = columnsCopy.map((column) => {
        column.hidden = !showHiddenColumns && column.isHiddenByDefault;
        return buildColumnByDataType(column);
    });

    const hasHiddenColumns = columnsCopy.some((column) => column.isHiddenByDefault);

    // Many tables will extend to the full width of the page but we also
    // want to keep some reasonable max bounds for smaller tables
    const defaultMaxWidth = isMenuOpen
        ? window.innerWidth * 0.79
        : window.innerWidth * 0.9;
    const tableMaxWidth = columnsCopy.filter(x => !x.hidden).length * 300;
    const maxWidth = Math.min(defaultMaxWidth, tableMaxWidth);

    const dataTableRef = useRef(null);
    const exportToCsv = () => {
        dataTableRef.current.exportCSV();
    };

    // Dates are stored as JS Date objects so the default CSV export would emit
    // the raw Date.toString(); format them the same way the table displays them
    const exportFunction = ({ data, field }) => {
        if (!(data instanceof Date)) {
            return data;
        }

        const column = columnsCopy.find((x) => x.field === field);
        return column?.dataType === 'date' ? formatDate(data) : formatDateTime(data);
    };

    return (
        <div className={`${rootClassName}`} style={{ maxWidth: maxWidth }}>
            <div className="flex space-x-3">
                <Button
                    onClick={resetFilters}
                    variant="outlined"
                    id="reset-filters"
                    disabled={!dataCopy || dataCopy.length === 0}
                >
                    Reset Table Filters
                </Button>
                <Button
                    onClick={exportToCsv}
                    variant="outlined"
                    id="export-to-csv"
                    disabled={!dataCopy || dataCopy.length === 0}
                >
                    Export to CSV
                </Button>
                {hasHiddenColumns && (
                    <Button
                        onClick={toggleHiddenColumns}
                        variant="outlined"
                        id="toggle-hidden-columns"
                        disabled={!dataCopy || dataCopy.length === 0}
                    >
                        {showHiddenColumns ? 'Hide User Details' : 'Show User Details'}
                    </Button>
                )}
            </div>
            <DataTableWrapper
                value={dataCopy}
                filters={filters}
                onFilter={onFilter}
                onValueChange={onValueChange}
                ref={dataTableRef}
                customDataTableOptions={{ exportFunction }}
            >
                {childrenColumns}
            </DataTableWrapper>
        </div>
    );
};

export default StandardizedDataTable;
